import React, { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { AvailabilityObject, AvailabilitySlot } from '../../types/schemas/abc';
import { Button } from '../../styles/_app.style';
import { updateAvailabilitySeries } from '../../utils/availability';
import { freeRepeatOptions } from '../../components/timeline/RepeatDropdown';
import { DropdownMenu, DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import { Item } from '../../styles/navigation.style';
import { Content } from '../../components/navigation/navDropdowns/navDropdown.style';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { styled } from '../../styles/stitches.config';

interface AvailabilityStepProps {
  data?: { availability?: AvailabilityObject };
  onChange: (patch: { availability: AvailabilityObject }) => void;
  onValidityChange?: (isValid: boolean) => void;
}

const CustomDiv = styled('div', {
  paddingBottom: '8px',
  overflowX: 'auto',
  display: 'flex',
  '&::-webkit-scrollbar': { height: '5px' },
  '&::-webkit-scrollbar-track': { background: 'transparent', margin: 0 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: '10px',
    border: 'none',
  },
});

const DAYS = [
  { label: 'Monday',    value: 'monday',    index: 1 },
  { label: 'Tuesday',   value: 'tuesday',   index: 2 },
  { label: 'Wednesday', value: 'wednesday', index: 3 },
  { label: 'Thursday',  value: 'thursday',  index: 4 },
  { label: 'Friday',    value: 'friday',    index: 5 },
  { label: 'Saturday',  value: 'saturday',  index: 6 },
  { label: 'Sunday',    value: 'sunday',    index: 0 },
] as const;

const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const formatTime = (minutes: number) => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const generateId = () => `series_${Date.now()}`;

const getDayLabel = (slot: AvailabilitySlot, firstDay: string): string | null => {
  if (slot.repeat !== 'Weekly') return null;
  return DAYS.find(d => d.value === firstDay)?.label ?? firstDay;
};

export const AvailabilityStep: React.FC<AvailabilityStepProps> = ({
  data,
  onChange,
  onValidityChange,
}) => {
  const [selectedDay, setSelectedDay] = useState<typeof DAYS[number]['value']>('monday');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [repeat, setRepeat] = useState<typeof freeRepeatOptions[number]>('Weekly');
  const [isDayOpen, setIsDayOpen] = useState(false);
  const [isRepeatOpen, setIsRepeatOpen] = useState(false);

  const availability: AvailabilityObject = data?.availability || {
    monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [],
  };

  const handleAddRule = () => {
    const startM = toMinutes(startTime);
    const endM = toMinutes(endTime);

    if (endM <= startM) {
      alert('End time must be after start time');
      return;
    }

    const dayIndex = DAYS.find(d => d.value === selectedDay)?.index ?? 1;

    const newAvailability = updateAvailabilitySeries(availability, 'add', {
      id: generateId(),
      startTime: startM,
      endTime: endM,
      dayOfWeek: dayIndex,
      repeat: repeat as AvailabilitySlot['repeat'],
    });

    onChange({ availability: newAvailability });
    onValidityChange?.(true);
  };

  const handleRemoveRule = (slotId: string, slotRepeat: AvailabilitySlot['repeat']) => {
    const newAvailability = updateAvailabilitySeries(availability, 'remove', {
      id: slotId,
      repeat: slotRepeat,
      startTime: 0,
      endTime: 0,
    });

    onChange({ availability: newAvailability });
    const hasSlots = Object.values(newAvailability).some(arr => arr && arr.length > 0);
    onValidityChange?.(hasSlots);
  };

  // Collect unique series (avoid showing the same series on every day)
  const uniqueSlots: { slot: AvailabilitySlot; firstDay: string }[] = [];
  const seenIds = new Set<string>();

  DAYS.forEach(({ value: dayValue }) => {
    (availability[dayValue] || []).forEach(slot => {
      if (!seenIds.has(slot.id)) {
        seenIds.add(slot.id);
        uniqueSlots.push({ slot, firstDay: dayValue });
      }
    });
  });

  return (
    <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
      <style>{`
        .clean-time-input::-webkit-calendar-picker-indicator { display: none; -webkit-appearance: none; }
        .clean-time-input { appearance: none; -moz-appearance: textfield; }
      `}</style>

      {/* --- ENTRY ROW --- */}
      <div style={{
        background: '#f8fafc', padding: '1.5rem', borderRadius: '12px',
        border: '1px solid #e2e8f0', marginBottom: '2rem',
      }}>
        <h4 style={{ margin: '0 0 1.25rem 0', fontSize: '1rem', color: '#334155' }}>
          Add your general availability
        </h4>

        <CustomDiv style={{
          display: 'flex', gap: '12px', alignItems: 'flex-end',
          flexWrap: 'nowrap', overflowX: 'auto', width: '100%',
        }}>

          {/* DAY — grisé quand repeat !== 'Weekly' */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: repeat === 'Weekly' ? '#64748b' : '#cbd5e1', marginLeft: '4px' }}>
              DAY
            </span>
            <DropdownMenu open={isDayOpen} onOpenChange={setIsDayOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  icon
                  disabled={repeat !== 'Weekly'}
                  css={{
                    minWidth: '8.5rem', padding: '0 1rem',
                    justifyContent: 'space-between', gap: '0.25rem',
                    fontSize: '$sm', backgroundColor: 'white',
                    border: '1px solid #cbd5e1', height: '38px',
                    opacity: repeat !== 'Weekly' ? 0.4 : 1,
                    '&:hover': { backgroundColor: '#f8fafc' },
                  }}
                >
                  {DAYS.find(d => d.value === selectedDay)?.label}
                  {isDayOpen ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                </Button>
              </DropdownMenuTrigger>
              <Content align="start" style={{ minWidth: '8.5rem' }}>
                {DAYS.map(d => (
                  <Item key={d.value} onSelect={() => setSelectedDay(d.value)} css={{ fontSize: '$sm' }}>
                    {d.label}
                  </Item>
                ))}
              </Content>
            </DropdownMenu>
          </div>

          {/* FROM */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>FROM</span>
            <input type="time" className="clean-time-input" value={startTime}
              onChange={e => setStartTime(e.target.value)}
              style={{ padding: '0 8px', borderRadius: '6px', border: '1px solid #cbd5e1', height: '38px', fontSize: '14px', width: '80px', textAlign: 'center' }}
            />
          </div>

          {/* TO */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>TO</span>
            <input type="time" className="clean-time-input" value={endTime}
              onChange={e => setEndTime(e.target.value)}
              style={{ padding: '0 8px', borderRadius: '6px', border: '1px solid #cbd5e1', height: '38px', fontSize: '14px', width: '80px', textAlign: 'center' }}
            />
          </div>

          {/* RECURRENCE */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginLeft: '4px' }}>RECURRENCE</span>
            <DropdownMenu open={isRepeatOpen} onOpenChange={setIsRepeatOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  icon
                  css={{
                    minWidth: '8.5rem', padding: '0 1rem',
                    justifyContent: 'space-between', gap: '0.25rem',
                    fontSize: '$sm', backgroundColor: 'white',
                    border: '1px solid #cbd5e1', height: '38px',
                    '&:hover': { backgroundColor: '#f8fafc' },
                  }}
                >
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left', flex: 1, minWidth: 0 }}>
                    {repeat}
                  </span>
                  {isRepeatOpen ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                </Button>
              </DropdownMenuTrigger>
              <Content align="start" style={{ minWidth: '8.5rem' }}>
                {freeRepeatOptions.map(opt => (
                  <Item key={opt} onSelect={() => setRepeat(opt as typeof freeRepeatOptions[number])} css={{ fontSize: '$sm' }}>
                    {opt}
                  </Item>
                ))}
              </Content>
            </DropdownMenu>
          </div>

        </CustomDiv>

        <Button
          type="button"
          onClick={handleAddRule}
          tone="success"
          css={{ height: '38px', minWidth: '42px', padding: '0', marginTop: '1rem', alignSelf: 'flex-end' }}
        >
          <Plus size={22} />
        </Button>
      </div>

      {/* --- LIST --- */}
      <div>
        <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#334155' }}>Your Availability</h4>

        {uniqueSlots.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No availability added yet.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {uniqueSlots.map(({ slot, firstDay }) => (
            <div key={slot.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', background: 'white',
              border: '1px solid #e2e8f0', borderRadius: '10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Jour — seulement pour 'Weekly' */}
                {getDayLabel(slot, firstDay) && (
                  <>
                    <span style={{ fontWeight: 500, color: '#1e293b' }}>
                      {getDayLabel(slot, firstDay)}
                    </span>
                    <span style={{ color: '#cbd5e1' }}>·</span>
                  </>
                )}
                {/* Horaire */}
                <span style={{ fontWeight: 500, color: '#1e293b' }}>
                  {formatTime(slot.start)} – {formatTime(slot.end)}
                </span>
                {/* Badge récurrence */}
                <span style={{
                  fontSize: '0.7rem', background: '#f1f5f9',
                  padding: '2px 8px', borderRadius: '12px', color: '#64748b',
                }}>
                  {slot.repeat}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveRule(slot.id, slot.repeat)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                title="Remove series"
              >
                <Trash2 size={18} style={{ stroke: 'red' }} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};